import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import backArrow from "../../assets/images/backArrow.svg";
import { buildSessionPath, getStoredSession } from "../mmtUserList/mmtFlow";
import {
  fetchLoggedUserInfo,
  getPatientDetails,
  removePatient,
  updateCognitivePatient,
  updatePatient,
} from "../mmtUserList/mmtApi";
import { USER_LIST } from "../../routes/routerPath";
import { isWebView, postWebViewMessage } from "../../utlis/webViewBridge";
import {
  getPersonTerminology,
  resolveCustomerType,
} from "../../utlis/personTerminology";
import { useT } from "../../i18n/useT";
import { getActiveLocale } from "../../configs/regionalProfile";
import { getCurrentLocale } from "../../utlis/locale";
import { isLastNameFirst } from "../../utlis/nameOrder";
import ModalWrap from "../modals/ModalWrap";
import {
  MAX_HEIGHT_CM,
  MAX_HEIGHT_FEET,
  MAX_WEIGHT_KG,
  MAX_WEIGHT_LBS,
  MIN_HEIGHT_CM,
  MIN_HEIGHT_FEET,
  MIN_WEIGHT_KG,
  MIN_WEIGHT_LBS,
  centimetersToFeetAndInches,
  feetAndInchesToCentimeters,
  isHeightCentimetersInRange,
  isWeightKilogramsInRange,
  isWeightPoundsInRange,
  kilogramsToPounds,
  poundsToKilograms,
  usesMetricMeasurements,
} from "./measurementUnits";
import { getContactFieldRules } from "./contactFieldRules";
import { BLOOD_PRESSURE_VALUES } from "../mmtUserList/mmtApi";
import {
  COGNITIVE_CUSTOMER_TYPE,
  COGNITIVE_DIAGNOSES,
  COGNITIVE_DIAGNOSIS_FIELD,
  COGNITIVE_MEDICATIONS,
  DIAGNOSIS_DATE_FIELD,
} from "./cognitivePatientFields";
import DiagnosisDateField from "./DiagnosisDateField";
import "./testperson.scss";

const GENDERS = ["male", "female", "other"];
// Cognitive accounts: the backend accepts only male/female.
const BIOLOGICAL_GENDERS = ["male", "female"];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 80 }, (_, i) => currentYear - 21 - i);

const EXERCISES = ["low", "moderate", "good"];

const INJURIES = [
  "knee",
  "hip",
  "heart",
  "shoulder",
  "ankle",
  "eye",
  "arm",
  "hand_wrist",
  "stomach_intestine",
  "lungs",
  "other",
];

const MEDICATIONS = [
  "antiseizure",
  "diabetes",
  "pain_muscle",
  "mood_psych",
  "sedatives",
  "bp_heart",
];

const pickField = (fields, name) =>
  Array.isArray(fields)
    ? fields.find((f) => (f.uniqueId ?? f.name) === name)?.value
    : fields?.[name];

const hydrate = (detail) => {
  const fields = detail?.data || detail || [];
  const arr = Array.isArray(fields) ? fields : [];
  const val = (name) => pickField(arr, name) ?? detail?.[name];
  const metric = usesMetricMeasurements();
  const height = val("height") || "";
  const weight = val("weight") || "";
  const imperialHeight = centimetersToFeetAndInches(height);
  return {
    step1: {
      firstname: val("first_name") || "",
      lastname: val("last_name") || "",
      testPersonId: val("user_id_from_client") || val("test_person_id") || "",
      /**
       * BACKEND DATA-CONTRACT DEPENDENCY.
       *
       * System-generated by SeniorLife, distinct from the customer's External
       * ID, never entered by a user, not part of the registration gate.
       *
       * `seniorlife_id` is the field this UI expects, but it is NOT proven:
       * it does not appear in API_CONTRACT.md, in the v92 source, or in any
       * response shape supplied with this handoff — only in the standalone
       * mock's own fixture. A second guess (`senior_id`) was removed rather
       * than left in, because guessing two names hides the gap instead of
       * showing it. If the real response uses a different name, change this
       * one line; the UI does not need to change.
       *
       * When absent, the field renders a "not available" note rather than an
       * empty box, so a missing contract is visible instead of looking like a
       * person with no ID.
       */
      seniorlifeId: val("seniorlife_id") || "",
      gender: val("gender") || "",
      birthYear: val("birth_year") || "",
      birthMonth: val("birth_month") || "",
      cognitiveDiagnosis: val(COGNITIVE_DIAGNOSIS_FIELD) || "",
      diagnosisDate: val(COGNITIVE_DIAGNOSIS_FIELD)
        ? val(DIAGNOSIS_DATE_FIELD) || ""
        : "",
    },
    // GET /v4 only returns a `cognitive_diagnosis` widget for patients owned
    // by a cognitive account, so its presence identifies one even when the
    // staff profile isn't cached.
    hasCognitiveDiagnosis: arr.some(
      (f) => (f.uniqueId ?? f.name) === COGNITIVE_DIAGNOSIS_FIELD,
    ),
    step2: {
      zipcode: val("zip_code") || "",
      email: val("email") || "",
      phone: val("mobile_phone_number") || "",
      height: metric ? height : "",
      heightFeet: metric ? "" : imperialHeight.feet,
      heightInches: metric ? "" : imperialHeight.inches,
      weight: metric ? weight : kilogramsToPounds(weight),
      exercises: val("fitness_level") || "",
      injuries: val("injury_or_surgery") || [],
      medications: val("medications") || [],
      // Blood Pressure Profile — an ordinary optional person field. "" = not set.
      bloodPressure: val("blood_pressure_profile") || "",
    },
  };
};

const getCachedAccount = () => {
  try {
    return JSON.parse(sessionStorage.getItem("staffDetails") || "null");
  } catch {
    return null;
  }
};

const isValidName = (s) => /^[\p{L}\p{M}\s'-]+$/u.test(s);
const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
export default function MmtEditTestPerson({
  readOnly = false,
  seniorUuidProp,
  onClose,
  onSuccess,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useT("testPersonForm");
  const MONTHS = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleString(getActiveLocale(), {
      month: "short",
    }),
  }));
  // Units and contact rules come from the regional profile, never `locale`.
  const metricMeasurements = usesMetricMeasurements();
  const contactRules = getContactFieldRules();
  const seniorUuid = seniorUuidProp ?? searchParams.get("seniorUuid");
  const [session] = useState(getStoredSession);
  const [step1, setStep1] = useState({
    firstname: "",
    lastname: "",
    testPersonId: "",
    seniorlifeId: "",
    gender: "",
    birthYear: "",
    birthMonth: "",
    cognitiveDiagnosis: "",
    diagnosisDate: "",
  });
  const [step2, setStep2] = useState({
    zipcode: "",
    email: "",
    phone: "",
    height: "",
    heightFeet: "",
    heightInches: "",
    weight: "",
    exercises: "",
    injuries: [],
    medications: [],
    // Blood Pressure Profile — optional self-reported person field. "" = not set.
    bloodPressure: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");
  // `field` from a backend 400 (cognitive validation) — highlights that input.
  const [serverField, setServerField] = useState("");
  const [patientIsCognitive, setPatientIsCognitive] = useState(false);
  const [account, setAccount] = useState(getCachedAccount);
  const personLabels = getPersonTerminology(account);
  const isCognitive =
    resolveCustomerType(account) === COGNITIVE_CUSTOMER_TYPE ||
    patientIsCognitive;
  const genders = isCognitive ? BIOLOGICAL_GENDERS : GENDERS;
  // Opened straight by the iPad app (not as the user list's modal). Close
  // events go to the native shell for cognitive customers only; every other
  // customer type keeps the normal web navigation.
  const nativeCompletion = isWebView() && !onClose && !onSuccess && isCognitive;

  useEffect(() => {
    if (!session || !seniorUuid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    // Fetch the account alongside the patient: `fetchLoggedUserInfo` applies
    // the account's region, which decides cm/kg vs ft-in/lbs. It must land
    // before `hydrate()` converts the stored cm/kg, or a direct deep link into
    // Edit (no user list visited, nothing cached) would fall back to US units.
    // A failed account fetch keeps the cached region; it never blocks editing.
    const accountRequest = fetchLoggedUserInfo(session)
      .then((details) => {
        if (details) {
          sessionStorage.setItem("staffDetails", JSON.stringify(details));
        }
        return details;
      })
      .catch(() => null);
    Promise.all([accountRequest, getPatientDetails(session, seniorUuid)])
      .then(([details, detail]) => {
        if (cancelled) return;
        if (details) setAccount(details);
        const next = hydrate(detail);
        const cognitive =
          resolveCustomerType(details || getCachedAccount()) ===
            COGNITIVE_CUSTOMER_TYPE || next.hasCognitiveDiagnosis;
        setPatientIsCognitive(next.hasCognitiveDiagnosis);
        setStep1(
          // A legacy "other" can't be saved for a cognitive patient; make the
          // user pick male/female instead of failing on save.
          cognitive && next.step1.gender === "other"
            ? { ...next.step1, gender: "" }
            : next.step1,
        );
        // Blood Pressure Profile hydrates from the person field, like any other.
        setStep2(next.step2);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              (!err?.response
                ? t("network_error")
                : t("load_error", {
                    person:
                      getPersonTerminology(getCachedAccount()).singularLower,
                  })),
          );
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // `locale` is no longer a dependency: hydration converts using the
    // regional profile, so changing UI language cannot alter the loaded values.
  }, [session, seniorUuid, t]);

  const clearServerField = (...keys) =>
    setServerField((prev) => (keys.includes(prev) ? "" : prev));
  const setF1 = (key, value) => {
    clearServerField(
      key,
      "gender",
      COGNITIVE_DIAGNOSIS_FIELD,
      DIAGNOSIS_DATE_FIELD,
    );
    setStep1((prev) => {
      const next = { ...prev, [key]: value };
      if (!next.cognitiveDiagnosis) next.diagnosisDate = "";
      return next;
    });
  };
  const setF2 = (key, value) => setStep2((prev) => ({ ...prev, [key]: value }));
  const toggle = (key, value) => {
    if (key === "medications") clearServerField("medications");
    setStep2((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((v) => v !== value)
        : [...prev[key], value],
    }));
  };

  const back = () => {
    if (nativeCompletion) {
      postWebViewMessage("EDIT_TEST_PERSON_CANCELLED");
      return;
    }
    if (onClose) {
      onClose();
      return;
    }
    navigate(-1);
  };

  const done = () => {
    sessionStorage.removeItem("userList");
    if (onSuccess) {
      onSuccess();
      return;
    }
    navigate(buildSessionPath(USER_LIST, session), { replace: true });
  };

  const submit = async () => {
    if (!isFormValid || !session || !seniorUuid) return;
    setSaving(true);
    setError("");
    setServerField("");
    try {
      const measurements = metricMeasurements
        ? step2
        : {
            ...step2,
            height: feetAndInchesToCentimeters(
              step2.heightFeet,
              step2.heightInches,
            ),
            weight: step2.weight ? poundsToKilograms(step2.weight) : "",
          };
      // Blood Pressure Profile is part of `measurements` (step2) and is written
      // in the same person payload as every other field — no separate call.
      if (isCognitive) {
        const response = await updateCognitivePatient(
          session,
          seniorUuid,
          step1,
          measurements,
        );
        // Legacy: an unknown seniorUuid comes back 200 with data.success=false.
        if (response?.data?.success === false) {
          setError(t("save_error"));
          return;
        }
      } else {
        await updatePatient(session, seniorUuid, step1, measurements);
      }
      if (nativeCompletion) {
        sessionStorage.removeItem("userList");
        postWebViewMessage("TEST_PERSON_UPDATED", { seniorUuid });
        return;
      }
      done();
    } catch (err) {
      setServerField(err?.response?.data?.field || "");
      setError(
        err?.response?.data?.message ||
          (!err?.response ? t("network_error") : t("save_error")),
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!session || !seniorUuid || removing) return;
    setRemoving(true);
    setError("");
    try {
      await removePatient(session, seniorUuid);
      setRemoveOpen(false);
      done();
    } catch (err) {
      setRemoveOpen(false);
      setError(
        err?.response?.data?.message ||
          (!err?.response
            ? t("network_error")
            : t("remove_error", { person: personLabels.singularLower })),
      );
    } finally {
      setRemoving(false);
    }
  };

  if (!session && !isWebView()) return null;

  // ── Field-level validation (only fires when the field has content) ──────────
  const firstNameError =
    step1.firstname.trim() !== "" && !isValidName(step1.firstname);
  const lastNameError =
    step1.lastname.trim() !== "" && !isValidName(step1.lastname);
  const emailError =
    step2.email.trim() !== "" && !isValidEmail(step2.email.trim());
  const phoneError =
    step2.phone.trim() !== "" && !contactRules.isValidPhone(step2.phone);
  const zipcodeError =
    step2.zipcode.trim() !== "" && !contactRules.isValidPostal(step2.zipcode);
  const metricHeightError =
    step2.height !== "" && !isHeightCentimetersInRange(step2.height);
  const imperialHeightComplete =
    step2.heightFeet !== "" && step2.heightInches !== "";
  const imperialHeightCm = imperialHeightComplete
    ? feetAndInchesToCentimeters(step2.heightFeet, step2.heightInches)
    : null;
  const imperialHeightError =
    imperialHeightComplete && !isHeightCentimetersInRange(imperialHeightCm);
  const heightError = metricMeasurements
    ? metricHeightError
    : imperialHeightError;
  const weightError =
    step2.weight !== "" &&
    !(metricMeasurements
      ? isWeightKilogramsInRange(step2.weight)
      : isWeightPoundsInRange(step2.weight));

  const isFormValid =
    step1.firstname.trim() !== "" &&
    !firstNameError &&
    step1.lastname.trim() !== "" &&
    !lastNameError &&
    step1.gender !== "" &&
    step1.birthYear !== "" &&
    step1.birthMonth !== "" &&
    (!isCognitive || step1.cognitiveDiagnosis !== "") &&
    (metricMeasurements ? step2.height !== "" : imperialHeightComplete) &&
    !heightError &&
    step2.weight !== "" &&
    !weightError &&
    // Contact fields aren't shown for cognitive patients, so they can't block.
    (isCognitive || (!emailError && !phoneError && !zipcodeError));

  const disabled = readOnly || saving;

  const heightField = (
    <div className="mmt-field">
      <span>
        {t("height", {
          unit: metricMeasurements ? t("unit_cm") : t("unit_ft_in"),
        })}{" "}
        {!readOnly && "*"}
      </span>
      {metricMeasurements ? (
        <input
          type="number"
          min={MIN_HEIGHT_CM}
          max={MAX_HEIGHT_CM}
          step="0.1"
          value={step2.height}
          onChange={(e) => setF2("height", e.target.value)}
          className={heightError ? "mmt-input-error" : ""}
          autoFocus={searchParams.get("focus") === "height"}
          disabled={disabled}
        />
      ) : (
        <div className="mmt-height-imperial">
          <label>
            <input
              type="number"
              min={MIN_HEIGHT_FEET}
              max={MAX_HEIGHT_FEET}
              step="1"
              value={step2.heightFeet}
              onChange={(e) => setF2("heightFeet", e.target.value)}
              className={heightError ? "mmt-input-error" : ""}
              autoFocus={searchParams.get("focus") === "height"}
              disabled={disabled}
              aria-label={t("height_feet_aria")}
            />
            <span>{t("unit_ft")}</span>
          </label>
          <label>
            <input
              type="number"
              min="0"
              max="11"
              step="1"
              value={step2.heightInches}
              onChange={(e) => setF2("heightInches", e.target.value)}
              className={heightError ? "mmt-input-error" : ""}
              disabled={disabled}
              aria-label={t("height_inches_aria")}
            />
            <span>{t("unit_in")}</span>
          </label>
        </div>
      )}
      {heightError ? (
        <em className="mmt-tp-hint">
          {metricMeasurements
            ? t("height_hint_metric")
            : t("height_hint_imperial")}
        </em>
      ) : null}
    </div>
  );

  const weightField = (
    <div className="mmt-field">
      <span>
        {t("weight", {
          unit: metricMeasurements ? t("unit_kg") : t("unit_lbs"),
        })}{" "}
        {!readOnly && "*"}
      </span>
      <input
        type="number"
        min={metricMeasurements ? MIN_WEIGHT_KG : MIN_WEIGHT_LBS}
        max={metricMeasurements ? MAX_WEIGHT_KG : MAX_WEIGHT_LBS}
        step="0.1"
        value={step2.weight}
        onChange={(e) => setF2("weight", e.target.value)}
        className={weightError ? "mmt-input-error" : ""}
        disabled={disabled}
      />
      {weightError ? (
        <em className="mmt-tp-hint">
          {t("weight_hint", {
            min: metricMeasurements ? MIN_WEIGHT_KG : MIN_WEIGHT_LBS,
            max: metricMeasurements ? MAX_WEIGHT_KG : MAX_WEIGHT_LBS,
            unit: metricMeasurements ? t("unit_kg") : t("unit_lbs"),
          })}
        </em>
      ) : null}
    </div>
  );

  const firstNameField = (
    <div className="mmt-field" key="firstname">
      <span>
        {t("first_name")} {!readOnly && "*"}
      </span>
      <input
        value={step1.firstname}
        onChange={(e) => setF1("firstname", e.target.value)}
        maxLength={50}
        className={firstNameError ? "mmt-input-error" : ""}
        disabled={disabled}
      />
      {firstNameError ? (
        <em className="mmt-tp-hint">{t("name_hint")}</em>
      ) : null}
    </div>
  );
  const lastNameField = (
    <div className="mmt-field" key="lastname">
      <span>
        {t("last_name")} {!readOnly && "*"}
      </span>
      <input
        value={step1.lastname}
        onChange={(e) => setF1("lastname", e.target.value)}
        maxLength={50}
        className={lastNameError ? "mmt-input-error" : ""}
        disabled={disabled}
      />
      {lastNameError ? <em className="mmt-tp-hint">{t("name_hint")}</em> : null}
    </div>
  );
  // Entry order follows display order (`formatName`): family name first in ja/zh.
  const nameFields = isLastNameFirst(getCurrentLocale())
    ? [lastNameField, firstNameField]
    : [firstNameField, lastNameField];

  return (
    <main className="mmt-test-person-page">
      <header className="mmt-tp-header">
        <button onClick={back} aria-label={t("back")} className="mmt-tp-back">
          <img src={backArrow} alt="" />
        </button>
        <h1>
          {readOnly
            ? personLabels.singular
            : t("edit_person", { person: personLabels.singular })}
        </h1>
      </header>

      {loading ? (
        <section className="mmt-tp-form">
          <p>{t("loading")}</p>
        </section>
      ) : (
        <section className="mmt-tp-form">
          {error ? <div className="mmt-tp-error">{error}</div> : null}

          {/* ── Patient Info ── */}
          <div className="mmt-tp-section">
            <div className="mmt-tp-section-label">
              {t("patient_info", { person: personLabels.singular })}
            </div>

            <div className="mmt-tp-row">{nameFields}</div>

            <div className="mmt-field">
              <span>{t("person_id", { person: personLabels.singular })}</span>
              <input
                value={step1.testPersonId}
                disabled
                data-field="externalId"
              />
            </div>

            {!isCognitive ? (
              <div className="mmt-field">
                <span>{t("seniorlife_id")}</span>
                <input
                  value={step1.seniorlifeId}
                  disabled
                  data-field="seniorlifeId"
                  placeholder={t("seniorlife_id_unavailable")}
                />
                <em className="mmt-tp-readonly-note">
                  {t("seniorlife_id_note")}
                </em>
              </div>
            ) : null}

            <div className="mmt-field">
              <span>
                {t(isCognitive ? "biological_gender" : "gender")}{" "}
                {!readOnly && "*"}
              </span>
              <div
                className={`mmt-tp-seg${
                  serverField === "gender" ? " mmt-input-error" : ""
                }`}
              >
                {genders.map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={`mmt-tp-seg-btn${step1.gender === g ? " selected" : ""}`}
                    onClick={() => setF1("gender", g)}
                    disabled={disabled}
                  >
                    {t(g)}
                  </button>
                ))}
              </div>
            </div>

            <div className="mmt-tp-row">
              <label className="mmt-field">
                <span>
                  {t("birth_year")} {!readOnly && "*"}
                </span>
                <select
                  value={step1.birthYear}
                  onChange={(e) =>
                    setF1(
                      "birthYear",
                      e.target.value ? Number(e.target.value) : "",
                    )
                  }
                  disabled={disabled}
                >
                  <option value="">{t("year")}</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mmt-field">
                <span>
                  {t("birth_month")} {!readOnly && "*"}
                </span>
                <select
                  value={step1.birthMonth}
                  onChange={(e) =>
                    setF1(
                      "birthMonth",
                      e.target.value ? Number(e.target.value) : "",
                    )
                  }
                  disabled={disabled}
                >
                  <option value="">{t("month")}</option>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {isCognitive ? (
            <div className="mmt-tp-section">
              <div className="mmt-tp-row">
                {heightField}
                {weightField}
              </div>
            </div>
          ) : (
            <>
              {/* ── Demographics ── */}
              <div className="mmt-tp-section">
                <div className="mmt-tp-section-label">{t("demographics")}</div>

                <div className="mmt-tp-row">
                  <div className="mmt-field">
                    <span>{t("zip_code")}</span>
                    <input
                      value={step2.zipcode}
                      onChange={(e) =>
                        setF2(
                          "zipcode",
                          contactRules.sanitizePostal(e.target.value),
                        )
                      }
                      inputMode={contactRules.postalInputMode}
                      maxLength={contactRules.postalMaxLength}
                      className={zipcodeError ? "mmt-input-error" : ""}
                      disabled={disabled}
                    />
                    {zipcodeError ? (
                      <em className="mmt-tp-hint">{t("zip_hint")}</em>
                    ) : null}
                  </div>
                  <div className="mmt-field">
                    <span>{t("email")}</span>
                    <input
                      type="email"
                      inputMode="email"
                      value={step2.email}
                      onChange={(e) => setF2("email", e.target.value)}
                      className={emailError ? "mmt-input-error" : ""}
                      disabled={disabled}
                    />
                    {emailError ? (
                      <em className="mmt-tp-hint">{t("email_hint")}</em>
                    ) : null}
                  </div>
                </div>

                <div className="mmt-tp-row">
                  <div className="mmt-field">
                    <span>{t("phone_number")}</span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={step2.phone}
                      onChange={(e) =>
                        setF2(
                          "phone",
                          contactRules.sanitizePhone(e.target.value),
                        )
                      }
                      maxLength={contactRules.phoneMaxLength}
                      className={phoneError ? "mmt-input-error" : ""}
                      disabled={disabled}
                    />
                    {phoneError ? (
                      <em className="mmt-tp-hint">{t("phone_hint")}</em>
                    ) : null}
                  </div>
                  {heightField}
                </div>

                <div className="mmt-tp-row">
                  {weightField}
                  <div className="mmt-field">
                    <span>{t("exercise_frequency")}</span>
                    <div className="mmt-tp-seg">
                      {EXERCISES.map((e) => (
                        <button
                          key={e}
                          type="button"
                          className={`mmt-tp-seg-btn${step2.exercises === e ? " selected" : ""}`}
                          onClick={() => setF2("exercises", e)}
                          disabled={disabled}
                        >
                          {t(`exercise_${e}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Blood Pressure Profile (optional person field) ── */}
              <div className="mmt-tp-section">
                <div className="mmt-tp-section-label">
                  {t("blood_pressure")}
                </div>
                <div className="mmt-tp-seg" data-field="bloodPressure">
                  {BLOOD_PRESSURE_VALUES.map((b) => (
                    <button
                      key={b}
                      type="button"
                      className={`mmt-tp-seg-btn${step2.bloodPressure === b ? " selected" : ""}`}
                      onClick={() =>
                        setF2(
                          "bloodPressure",
                          step2.bloodPressure === b ? "" : b,
                        )
                      }
                      disabled={disabled}
                    >
                      {t(`bp_${b}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Injuries / Surgeries ── */}
              <div className="mmt-tp-section">
                <div className="mmt-tp-section-label">
                  {t("injuries_surgeries")}
                </div>
                <div className="mmt-tp-chip-group">
                  {INJURIES.map((i) => (
                    <button
                      key={i}
                      type="button"
                      className={`mmt-tp-chip${step2.injuries.includes(i) ? " selected" : ""}`}
                      onClick={() => toggle("injuries", i)}
                      disabled={disabled}
                    >
                      {t(`injury_${i}`)}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {isCognitive ? (
            <div className="mmt-tp-section">
              <label className="mmt-field">
                <span>
                  {t("cognitive_diagnosis")} {!readOnly && "*"}
                </span>
                <select
                  value={step1.cognitiveDiagnosis}
                  onChange={(e) => setF1("cognitiveDiagnosis", e.target.value)}
                  className={
                    serverField === COGNITIVE_DIAGNOSIS_FIELD
                      ? "mmt-input-error"
                      : ""
                  }
                  disabled={disabled}
                >
                  <option value="">{t("select_diagnosis")}</option>
                  {COGNITIVE_DIAGNOSES.map((diagnosis) => (
                    <option key={diagnosis} value={diagnosis}>
                      {t(`diagnosis_${diagnosis}`)}
                    </option>
                  ))}
                </select>
              </label>
              <DiagnosisDateField
                value={step1.diagnosisDate}
                onChange={(value) => setF1("diagnosisDate", value)}
                disabled={disabled || !step1.cognitiveDiagnosis}
                invalid={serverField === DIAGNOSIS_DATE_FIELD}
              />
            </div>
          ) : null}

          {/* ── Medications ── */}
          <div className="mmt-tp-section">
            <div className="mmt-tp-section-label">{t("medications")}</div>
            <div
              className={`mmt-tp-chip-group${
                serverField === "medications" ? " mmt-input-error" : ""
              }`}
            >
              {(isCognitive ? COGNITIVE_MEDICATIONS : MEDICATIONS).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`mmt-tp-chip${step2.medications.includes(m) ? " selected" : ""}`}
                  onClick={() => toggle("medications", m)}
                  disabled={disabled}
                >
                  {t(`med_${m}`)}
                </button>
              ))}
            </div>
          </div>

          {!readOnly ? (
            <div className="mmt-tp-actions">
              <button
                type="button"
                className="mmt-primary-button"
                onClick={submit}
                disabled={!isFormValid || saving}
              >
                {saving ? t("saving") : t("save")}
              </button>
              <button
                type="button"
                className="mmt-danger-button"
                onClick={() => setRemoveOpen(true)}
                disabled={saving || removing}
              >
                {t("remove_person", { person: personLabels.singular })}
              </button>
            </div>
          ) : null}
        </section>
      )}
      <ModalWrap
        className="mmt-remove-person-modal"
        open={removeOpen}
        onClose={() => !removing && setRemoveOpen(false)}
      >
        <div className="mmt-remove-person-dialog">
          <p>
            {t("remove_confirm", {
              person: personLabels.singularLower,
              name: `${step1.firstname} ${step1.lastname}`.trim(),
            })}
          </p>
          <div className="mmt-remove-person-actions">
            <button
              type="button"
              onClick={() => setRemoveOpen(false)}
              disabled={removing}
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              className="confirm"
              onClick={remove}
              disabled={removing}
            >
              {removing ? t("removing") : t("confirm_remove")}
            </button>
          </div>
        </div>
      </ModalWrap>
    </main>
  );
}
